import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { purchaseOrdersApi } from '../../api/endpoints';
import StatusBadge from '../../components/common/StatusBadge';
import {
  PurchaseOrderDetail,
} from '../../types';
import Select from '../../components/common/Select';
import { FiCheck, FiX, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { invoicesApi } from '../../api/endpoints';

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'items' | 'confirmations' | 'shipments' | 'invoices'>('items');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState<{
    status: string;
    notes: string;
    items: { itemId: string; confirmedQuantity: number }[];
  }>({ status: 'Accepted', notes: '', items: [] });
  const [submitting, setSubmitting] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await purchaseOrdersApi.get(id);
      setOrder(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad pobierania zamowienia');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const openConfirmModal = useCallback(() => {
    if (!order) return;
    setConfirmData({
      status: 'Accepted',
      notes: '',
      items: order.items.map((item) => ({
        itemId: item.id,
        confirmedQuantity: item.orderedQuantity,
      })),
    });
    setShowConfirmModal(true);
  }, [order]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await purchaseOrdersApi.confirm(id, confirmData);
      toast.success('Zamowienie potwierdzone');
      setShowConfirmModal(false);
      fetchOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad potwierdzania zamowienia');
    } finally {
      setSubmitting(false);
    }
  }, [id, confirmData, fetchOrder]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' });

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">Nie znaleziono zamowienia.</div>
        <Link to="/purchase-orders" className="btn btn-secondary">
          Powrot do listy
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/purchase-orders" className="btn btn-link">
            &larr; Zamowienia
          </Link>
          <h1>Zamowienie {order.amazonPurchaseOrderNumber}</h1>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-primary" onClick={openConfirmModal}>
            <FiCheck /> Potwierdz zamowienie
          </button>
          <button
            className="btn btn-secondary"
            onClick={async () => {
              if (!id) return;
              try {
                const res = await invoicesApi.generateFromPo(id);
                toast.success('Faktura wygenerowana z zamowienia');
                window.location.href = `/invoices/${res.data.id}`;
              } catch (err: any) {
                toast.error(err.response?.data?.message || 'Blad generowania faktury');
              }
            }}
          >
            <FiFileText /> Generuj fakture
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">Numer PO</span>
          <span className="detail-value">{order.amazonPurchaseOrderNumber}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Status</span>
          <span className="detail-value">
            <StatusBadge type="order" value={order.status} />
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Kanal</span>
          <span className="detail-value">
            <StatusBadge type="channel" value={order.channel} />
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Data zamowienia</span>
          <span className="detail-value">{formatDate(order.orderDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Oczekiwana dostawa</span>
          <span className="detail-value">{formatDate(order.expectedDeliveryDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Okno wysylki</span>
          <span className="detail-value">
            {formatDate(order.shipWindowStart)} - {formatDate(order.shipWindowEnd)}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Kwota</span>
          <span className="detail-value">{formatCurrency(order.totalAmount)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Waluta</span>
          <span className="detail-value">{order.currency}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Sprzedawca</span>
          <span className="detail-value">{order.sellingPartyId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Odbiorca (Ship To)</span>
          <span className="detail-value">{order.shipToPartyId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Adres wysylki</span>
          <span className="detail-value">{order.shipToAddress || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Platnik (Bill To)</span>
          <span className="detail-value">{order.billToPartyId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Synchronizacja</span>
          <span className="detail-value">{formatDate(order.syncedAt)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Utworzono</span>
          <span className="detail-value">{formatDate(order.createdAt)}</span>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          Pozycje ({order.items.length})
        </button>
        <button
          className={`tab ${activeTab === 'confirmations' ? 'active' : ''}`}
          onClick={() => setActiveTab('confirmations')}
        >
          Potwierdzenia ({order.confirmations.length})
        </button>
        <button
          className={`tab ${activeTab === 'shipments' ? 'active' : ''}`}
          onClick={() => setActiveTab('shipments')}
        >
          Wysylki ({order.shipments.length})
        </button>
        <button
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Faktury ({order.invoices.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'items' && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Produkt</th>
                  <th>ASIN</th>
                  <th>Zamowiono</th>
                  <th>Cena jedn.</th>
                  <th>Wartosc</th>
                  <th>Potwierdzone</th>
                  <th>Wyslane</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, idx) => (
                  <tr key={item.id}>
                    <td>{item.itemSequenceNumber}</td>
                    <td>{item.productTitle}</td>
                    <td>{item.asin || '-'}</td>
                    <td>
                      {item.orderedQuantity} {item.quantityUnitOfMeasure}
                    </td>
                    <td>{formatCurrency(item.unitPrice)}</td>
                    <td>{formatCurrency(item.totalPrice)}</td>
                    <td>{item.confirmedQuantity ?? '-'}</td>
                    <td>{item.shippedQuantity ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'confirmations' && (
          <div className="table-responsive">
            {order.confirmations.length === 0 ? (
              <p className="text-muted">Brak potwierdzeni.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Data potwierdzenia</th>
                    <th>Potwierdzil</th>
                  </tr>
                </thead>
                <tbody>
                  {order.confirmations.map((conf) => (
                    <tr key={conf.id}>
                      <td>
                        <span className="badge">{conf.status}</span>
                      </td>
                      <td>{formatDate(conf.confirmationDate)}</td>
                      <td>{conf.confirmedBy || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'shipments' && (
          <div className="table-responsive">
            {order.shipments.length === 0 ? (
              <p className="text-muted">Brak wysylek.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>ID wysylki Amazon</th>
                    <th>Status</th>
                    <th>Data wysylki</th>
                    <th>Przewoznik</th>
                    <th>Tracking</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {order.shipments.map((ship) => (
                    <tr key={ship.id}>
                      <td>{ship.amazonShipmentId || '-'}</td>
                      <td>
                        <span className="badge">{ship.status}</span>
                      </td>
                      <td>{formatDate(ship.shipDate)}</td>
                      <td>{ship.carrierName || '-'}</td>
                      <td>{ship.trackingNumber || '-'}</td>
                      <td>
                        <Link to={`/shipments/${ship.id}`} className="btn btn-sm btn-link">
                          Szczegoly
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="table-responsive">
            {order.invoices.length === 0 ? (
              <p className="text-muted">Brak faktur.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Numer faktury</th>
                    <th>Status</th>
                    <th>Data faktury</th>
                    <th>Kwota</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {order.invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>{inv.invoiceNumber}</td>
                      <td>
                        <span className="badge">{inv.status}</span>
                      </td>
                      <td>{formatDate(inv.invoiceDate)}</td>
                      <td>{formatCurrency(inv.totalAmount)}</td>
                      <td>
                        <Link to={`/invoices/${inv.id}`} className="btn btn-sm btn-link">
                          Szczegoly
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Potwierdz zamowienie</h2>
              <button
                className="btn btn-icon"
                onClick={() => setShowConfirmModal(false)}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Status potwierdzenia</label>
                <Select
                  options={[
                    { value: 'Accepted', label: 'Zaakceptowane' },
                    { value: 'Rejected', label: 'Odrzucone' },
                  ]}
                  value={confirmData.status}
                  onChange={(value) =>
                    setConfirmData((prev) => ({ ...prev, status: value }))
                  }
                />
              </div>

              <div className="form-group">
                <label>Pozycje</label>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Produkt</th>
                        <th>Zamowiono</th>
                        <th>Potwierdzono</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, idx) => (
                        <tr key={item.id}>
                          <td>{item.productTitle}</td>
                          <td>{item.orderedQuantity}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              min={0}
                              max={item.orderedQuantity}
                              value={confirmData.items[idx]?.confirmedQuantity ?? 0}
                              onChange={(e) => {
                                const newItems = [...confirmData.items];
                                newItems[idx] = {
                                  ...newItems[idx],
                                  confirmedQuantity: Number(e.target.value),
                                };
                                setConfirmData((prev) => ({
                                  ...prev,
                                  items: newItems,
                                }));
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="form-group">
                <label>Uwagi</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={confirmData.notes}
                  onChange={(e) =>
                    setConfirmData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Opcjonalne uwagi..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Anuluj
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmSubmit}
                disabled={submitting}
              >
                {submitting ? 'Wysylanie...' : 'Potwierdz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetailPage;
